export type PostStatus = 'Draft' | 'Scheduled' | 'Published' | 'Archived';
export const PostStatusValue: Record<PostStatus, number> = {
  Draft: 0,
  Scheduled: 1,
  Published: 2,
  Archived: 3,
};

export interface PagedList<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface ApiProblem {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  traceId?: string;
  errors?: Record<string, string[]>;
}

export interface CurrentUser {
  id: string;
  email: string;
  displayName: string;
  avatarUrl?: string | null;
  roles: string[];
  mustChangePassword?: boolean;
}

export type AppRole = 'Admin' | 'Editor';
export const ROLE_RANK: Record<AppRole, number> = { Admin: 100, Editor: 50 };
export const isAdmin = (roles: string[]) => roles.includes('Admin');
export const highestRank = (roles: string[]) =>
  roles.reduce((max, r) => Math.max(max, ROLE_RANK[r as AppRole] ?? 0), 0);

export interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  avatarUrl?: string | null;
  isActive: boolean;
  mustChangePassword: boolean;
  lastLoginUtc?: string | null;
  createdAtUtc: string;
  roles: string[];
}

export interface AdminUserDetail extends AdminUser {
  bio?: string | null;
  updatedAtUtc: string;
  passwordChangedAtUtc?: string | null;
  lockoutEndUtc?: string | null;
}

export interface SeoMetadata {
  title?: string | null;
  description?: string | null;
  keywords?: string | null;
  canonicalUrl?: string | null;
  ogImageUrl?: string | null;
  noIndex?: boolean;
}

export interface Tag {
  id: string;
  name: string;
  slug: string;
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  sortOrder: number;
}

export interface AdminPost {
  id: string;
  title: string;
  slug: string;
  authorName: string;
  categoryName?: string | null;
  coverImageKey?: string | null;
  status: number;
  isFeatured: boolean;
  viewCount: number;
  publishedAtUtc?: string | null;
  scheduledForUtc?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface PostDetail {
  id: string;
  title: string;
  slug: string;
  excerpt?: string | null;
  contentHtml: string;
  contentJson?: string | null;
  coverImageId?: string | null;
  coverImageKey?: string | null;
  coverBlurHash?: string | null;
  coverImageAlt?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  categorySlug?: string | null;
  authorName: string;
  authorAvatarUrl?: string | null;
  authorBio?: string | null;
  isFeatured: boolean;
  readingTimeMin: number;
  wordCount: number;
  viewCount: number;
  publishedAtUtc?: string | null;
  updatedAtUtc: string;
  tags: Tag[];
  seo: SeoMetadata;
  layoutKey?: string;
  isHowTo?: boolean;
}

export interface UploadedImage {
  id: string;
  objectKey: string;
  originalName: string;
  contentType: string;
  sizeBytes: number;
  width: number;
  height: number;
  blurHash?: string | null;
  altText?: string | null;
  createdAtUtc: string;
  url: string;
}
